import type { JobRecord } from "./types/job.js";
import type { SilentCronXHealth } from "./types/health.js";
import type { QueueStats } from "./types/queue.js";
import type { SilentCronXEventRecord } from "./types/events.js";

export type SilentCronXClientConfig = {
  baseUrl: string;
  headers?: Record<string, string>;
  fetcher?: typeof fetch;
};

export type TriggerJobRequest<TPayload = unknown> = {
  name?: string;
  payload?: TPayload;
  priority?: number;
};

export type TriggerJobResponse = {
  accepted: boolean;
  jobId: string;
};

export type SilentCronXClient = {
  triggerJob: <TPayload = unknown>(queueOrRoute: string, request?: TriggerJobRequest<TPayload>) => Promise<TriggerJobResponse>;
  getJob: <TPayload = unknown, TResult = unknown>(jobId: string) => Promise<JobRecord<TPayload, TResult> | null>;
  getJobs: () => Promise<JobRecord[]>;
  getHealth: () => Promise<SilentCronXHealth>;
  getQueueStats: (queueName?: string) => Promise<QueueStats[]>;
  getEvents: () => Promise<SilentCronXEventRecord[]>;
  cancelJob: (jobId: string) => Promise<{ cancelled: boolean; jobId: string }>;
};

export function createSilentCronXClient(config: SilentCronXClientConfig): SilentCronXClient {
  const baseUrl = config.baseUrl.replace(/\/+$/, "");
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const fetcher = config.fetcher ?? globalThis.fetch;
    if (!fetcher) throw new Error("SilentCronX client requires a fetch implementation");
    const response = await fetcher(`${baseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
        ...init.headers,
      },
    });
    if (!response.ok) throw new Error(`SilentCronX request failed: ${response.status}`);
    return (await response.json()) as T;
  };

  return {
    triggerJob: (queueOrRoute, body = {}) =>
      request<TriggerJobResponse>(`/jobs/${encodeURIComponent(queueOrRoute)}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    getJob: <TPayload = unknown, TResult = unknown>(jobId: string) =>
      request<JobRecord<TPayload, TResult> | null>(`/jobs/${encodeURIComponent(jobId)}`),
    getJobs: () => request<JobRecord[]>("/jobs"),
    getHealth: () => request<SilentCronXHealth>("/health"),
    getQueueStats: (queueName) =>
      request<QueueStats[]>(queueName ? `/queues/${encodeURIComponent(queueName)}/stats` : "/queues/stats"),
    getEvents: () => request<SilentCronXEventRecord[]>("/events"),
    cancelJob: (jobId) =>
      request<{ cancelled: boolean; jobId: string }>(`/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: "POST",
      }),
  };
}
